import { Palette, Plus, WalletCards } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { canUseCardWallet } from "@/app/auth/canUseCardWallet";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { hasStepUpMethod } from "@/shared/utils/stepUp";

import { ActivatePocketsBanner } from "../components/ActivatePocketsBanner";
import { CardWalletInviteCard } from "../components/card-wallet/CardWalletInviteCard";
import { CardRow } from "../components/CardRow";
import { CardsHeader } from "../components/CardsHeader";
import {
  CardsListFiltersBar,
  type CardListFilter,
  type CardListSortValue,
} from "../components/CardsListFiltersBar";
import { CardTile } from "../components/CardTile";
import { CreateCardModal } from "../components/CreateCardModal";
import { FirstCardEmptyState } from "../components/first-card/FirstCardEmptyState";
import { PocketsPreviewRow } from "../components/PocketsPreviewRow";
import { TwoFactorRequiredModal } from "../components/TwoFactorRequiredModal";
import { WalletBalanceBanner } from "../components/WalletBalanceBanner";
import { augmentCardWithMocks, type CardAugmented } from "../constants/cardMocks.constants";
import { useCardListPreferences } from "../hooks/useCardListPreferences";
import { useFirstCardRequirements } from "../hooks/useFirstCardRequirements";
import { usePocketsVisibility } from "../hooks/usePocketsVisibility";
import { CardsService } from "../services/cardsService";
import type {
  Card,
  CardListSortBy,
  CardListSortOrder,
  CardsCountsResponse,
  CardsFilters,
} from "../types/cards-api.types";

// The explainer carries its animation; only whoever opens it pays for it.
const EnableCardWalletDialog = lazy(() =>
  import("../components/card-wallet/EnableCardWalletDialog").then((module) => ({
    default: module.EnableCardWalletDialog,
  })),
);

const PAGE_SIZE = 24;

const CARD_LIST_FILTERS = [
  "all",
  "virtual",
  "fisica",
  "frozen",
  "pending",
  "no_pocket",
] as const satisfies readonly CardListFilter[];

const filterToServerParams = (filter: CardListFilter): Partial<CardsFilters> => {
  switch (filter) {
    case "virtual":
      return { card_type: "VIRTUAL" };
    case "fisica":
      return { card_type: "PHYSICAL" };
    case "frozen":
      return { status: "FROZEN" };
    case "pending":
      return { status: "PENDING_REVIEW" };
    case "no_pocket":
      return { no_wallet: true };
    case "all":
    default:
      return {};
  }
};

export const CardsPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const canCreateCard = can("cards:write") && can("cards:sensitive:write");
  const { visible: pocketsVisible } = usePocketsVisibility();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards/cards" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [cards, setCards] = useState<Card[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences, update: updatePreferences } = useCardListPreferences();
  const { view, sort_by: sortBy, order } = preferences;
  const sortValue = `${sortBy}:${order}` as CardListSortValue;
  const handleSortChange = useCallback(
    (next: CardListSortValue) => {
      const [nextSortBy, nextOrder] = next.split(":") as [CardListSortBy, CardListSortOrder];
      updatePreferences({ sort_by: nextSortBy, order: nextOrder });
    },
    [updatePreferences],
  );
  const [filter, setFilter] = useState<CardListFilter>(() => {
    const fromUrl = searchParams.get("filter");
    return fromUrl && (CARD_LIST_FILTERS as readonly string[]).includes(fromUrl)
      ? (fromUrl as CardListFilter)
      : "all";
  });
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState(() => (searchParams.get("q") ?? "").trim());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(timer);
  }, [query]);

  // The search term reaches the URL through the mirror, never from `onChange`: writing it per keystroke
  // spent one `history.replaceState` per character and tripped WebKit's 100-per-10-seconds limit, which
  // leaves react-router mid-navigation (see `useUrlParamMirror`). The filter below stays inline — it is one
  // write per click.
  useUrlParamMirror("q", query);

  const handleFilterChange = useCallback(
    (next: CardListFilter) => {
      setFilter(next);
      // The chips call this on every click, active or not, and react-router replaces the history entry even
      // when the URL is byte-identical — so re-picking the open filter used to cost one entry for nothing.
      // The comparison belongs out here: returning an unchanged object from the updater form still navigates.
      const nextParams = new URLSearchParams(searchParams);
      if (next === "all") {
        nextParams.delete("filter");
      } else {
        nextParams.set("filter", next);
      }
      if (nextParams.toString() === searchParams.toString()) return;

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [counts, setCounts] = useState<CardsCountsResponse>({
    all: 0,
    virtual: 0,
    fisica: 0,
    frozen: 0,
    pending: 0,
    no_pocket: 0,
  });
  const [countsLoaded, setCountsLoaded] = useState(false);

  // Drops stale responses when filters change mid-flight.
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (append: boolean, skip: number) => {
      const currentSeq = ++requestSeqRef.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }

      try {
        const filters: CardsFilters = {
          limit: PAGE_SIZE,
          skip,
          with_aggregates: true,
          sort_by: sortBy,
          order,
          ...filterToServerParams(filter),
          ...(debouncedQuery ? { q: debouncedQuery } : {}),
        };
        const response = await CardsService.fetchCards(filters);

        if (currentSeq !== requestSeqRef.current) return;

        const data = response.data;
        const list: Card[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? (data as unknown as Card[])
            : [];
        const nextTotal = typeof data?.total === "number" ? data.total : list.length;
        const nextHasMore =
          typeof data?.hasMore === "boolean" ? data.hasMore : skip + list.length < nextTotal;

        setCards((prev) => (append ? [...prev, ...list] : list));
        setTotal(nextTotal);
        setHasMore(nextHasMore);
      } catch (err: unknown) {
        if (currentSeq !== requestSeqRef.current) return;
        const message = HttpApiService.getErrorMessage(err) || t("cards_error_load");
        setError(message);
        if (!append) {
          setCards([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (currentSeq === requestSeqRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [t, filter, debouncedQuery, sortBy, order],
  );

  useEffect(() => {
    const currentSeq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const filters: CardsFilters = {
          limit: PAGE_SIZE,
          skip: 0,
          with_aggregates: true,
          sort_by: sortBy,
          order,
          ...filterToServerParams(filter),
          ...(debouncedQuery ? { q: debouncedQuery } : {}),
        };
        const response = await CardsService.fetchCards(filters);
        if (currentSeq !== requestSeqRef.current) return;
        const data = response.data;
        const list: Card[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? (data as unknown as Card[])
            : [];
        const nextTotal = typeof data?.total === "number" ? data.total : list.length;
        const nextHasMore = typeof data?.hasMore === "boolean" ? data.hasMore : list.length < nextTotal;
        setCards(list);
        setTotal(nextTotal);
        setHasMore(nextHasMore);
      } catch (err: unknown) {
        if (currentSeq !== requestSeqRef.current) return;
        setError(HttpApiService.getErrorMessage(err) || t("cards_error_load"));
        setCards([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        if (currentSeq === requestSeqRef.current) setIsLoading(false);
      }
    })();
  }, [filter, debouncedQuery, t, sortBy, order]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    fetchPage(true, cards.length);
  }, [isLoadingMore, hasMore, fetchPage, cards.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await CardsService.fetchCardsCounts();
        if (cancelled) return;
        setCounts(response.data);
        setCountsLoaded(true);
      } catch {
        // Chips will show 0; the listing still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter]);

  // The counts are organization-wide and ignore the filters, so they are what says whether the organization
  // has issued a card at all. Until they answer, neither the first-card state nor the wallet invite renders:
  // guessing either way would flash the wrong one.
  const hasNoCards = countsLoaded && counts.all === 0;
  const hasCards = countsLoaded && counts.all > 0;
  const firstCard = useFirstCardRequirements(hasNoCards);

  const augmentedCards = useMemo<CardAugmented[]>(
    () => cards.map((card, idx) => augmentCardWithMocks(card, idx)),
    [cards],
  );

  const handleOpen = useCallback(
    (cardId: string) => {
      navigate(`/app/cards/cards/${cardId}`);
    },
    [navigate],
  );

  const [isCreateCardModalOpen, setIsCreateCardModalOpen] = useState(false);
  const [isEnableWalletOpen, setIsEnableWalletOpen] = useState(false);
  const hasCardWallet = canUseCardWallet(user);
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);

  const stepUpReady = hasStepUpMethod(user);
  const handleCreateCardClick = useCallback(() => {
    if (!stepUpReady) {
      setIsTwoFactorModalOpen(true);
      return;
    }
    setIsCreateCardModalOpen(true);
  }, [stepUpReady]);

  // A security PIN created in the notice is all issuing a card needed: open the form it was asked for.
  const handleTwoFactorEnrolled = useCallback(() => {
    setIsTwoFactorModalOpen(false);
    setIsCreateCardModalOpen(true);
  }, []);

  const handleGoToTwoFactorSettings = useCallback(() => {
    setIsTwoFactorModalOpen(false);
    navigate("/app/settings?section=seguridad");
  }, [navigate]);

  const handleCardCreated = useCallback(
    (cardId: string) => {
      if (cardId) navigate(`/app/cards/cards/${cardId}`);
    },
    [navigate],
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <CardsHeader
        activeTabId="cards"
        actions={
          <div className="flex items-center gap-2">
            <ColocaButton variant="default" icon={Palette} onClick={() => navigate("/app/cards/branding")}>
              {t("branding_button_personalize")}
            </ColocaButton>
            {canCreateCard ? (
              <ColocaButton variant="primary" icon={Plus} onClick={handleCreateCardClick}>
                {t("card_new_button")}
              </ColocaButton>
            ) : (
              <ColocaTooltip
                trigger={
                  <ColocaButton variant="primary" icon={Plus} disabled>
                    {t("card_new_button")}
                  </ColocaButton>
                }
              >
                {t("no_permission_contact_admin")}
              </ColocaTooltip>
            )}
          </div>
        }
      />

      {/* Only an organization with the card wallet sees its balance; the others get one quiet line saying it
          exists — once they have a card, since moving balance between cards means nothing before the first.
          The dialog lives here, not in the invite, because enabling swaps the invite for the banner and would
          otherwise unmount the dialog before it shows its success. */}
      {hasCardWallet ? (
        <WalletBalanceBanner />
      ) : (
        hasCards && (
          <CardWalletInviteCard
            organizationId={user?.organization_id ?? null}
            onLearnMore={() => setIsEnableWalletOpen(true)}
          />
        )
      )}
      {isEnableWalletOpen && (
        <Suspense fallback={null}>
          <EnableCardWalletDialog open onClose={() => setIsEnableWalletOpen(false)} />
        </Suspense>
      )}

      <PocketsPreviewRow />

      <ActivatePocketsBanner />

      {hasNoCards ? (
        <FirstCardEmptyState
          hasSecurityMethod={firstCard.hasSecurityMethod}
          holderStatus={firstCard.holderStatus}
          canCreateCard={canCreateCard}
          onCreateCard={handleCreateCardClick}
          onViewHolders={() => navigate("/app/cards/identities")}
        />
      ) : (
        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold leading-tight text-foreground">
                {t("cards_list_section_title")}
              </h2>
              <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                {augmentedCards.length} {t("cards_list_of_total")} {total} {t("cards_list_results_label")}
              </p>
            </div>
          </div>

          <CardsListFiltersBar
            filter={filter}
            onFilterChange={handleFilterChange}
            query={query}
            onQueryChange={setQuery}
            view={view}
            onViewChange={(next) => updatePreferences({ view: next })}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            counts={counts}
            showProductFilters={counts.fisica > 0}
            showPocketFilter={pocketsVisible}
          />

          {error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : isLoading && cards.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">{t("cards_loading")}</span>
              </div>
            </div>
          ) : augmentedCards.length === 0 ? (
            <ColocaEmpty
              icon={WalletCards}
              title={t("cards_list_empty_title")}
              description={t("cards_list_empty_description")}
            />
          ) : (
            <>
              {view === "list" ? (
                <div className="space-y-2">
                  {augmentedCards.map((card) => (
                    <CardRow key={card.id} card={card} onOpen={handleOpen} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {augmentedCards.map((card) => (
                    <CardTile key={card.id} card={card} onOpen={handleOpen} />
                  ))}
                </div>
              )}

              {hasMore && (
                <div className="flex justify-center pt-2">
                  <ColocaButton variant="default" onClick={handleLoadMore} loading={isLoadingMore}>
                    {isLoadingMore ? t("cards_loading_more") : t("cards_load_more")}
                  </ColocaButton>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <CreateCardModal
        open={isCreateCardModalOpen}
        onOpenChange={setIsCreateCardModalOpen}
        onCreated={handleCardCreated}
        onRefresh={() => fetchPage(false, 0)}
      />
      <TwoFactorRequiredModal
        open={isTwoFactorModalOpen}
        onOpenChange={setIsTwoFactorModalOpen}
        reason="create_card"
        onGoToSettings={handleGoToTwoFactorSettings}
        onEnrolled={handleTwoFactorEnrolled}
      />
    </div>
  );
};
